import { constructAsync } from '../../../webmodule-common/other/async-constructor';
import { customElement, state } from 'lit/decorators.js';
import { DataEntryTabManagerView } from '../../../webmodule-common/other/ui/data-entry-screen-base';
import { FranchiseeNetworkApi } from '../../api/franchisee-network-api';
import { getApiFactory } from '../../api/api-injector';
import { getSupportTypeDisplayValue } from '../data/support-helper';
import { QuoteSupportInboxViewTab } from './quote-support-inbox-view-tab';
import { TabManager } from '../../../webmodule-common/other/ui/tab-manager/tab-manager';
import type { TabPage } from '../../../webmodule-common/other/ui/tab-manager/tab-manager-types';
import { SupportInboxItemStatus, SupportInboxItemType } from '../../api/supplier-api-interface-franchiseenetwork';
import { tlang } from '../../../webmodule-common/other/language/lang';

import { loginAsFranchiseeForSupportTicket } from '../../common/franchisee-login';
import { getTerritoryManager } from '../../settings/data/territory-manager';
import { claimIdentity } from '../../../webmodule-common/other/api/user-security';
import { validId } from '../../../webmodule-common/other/ui/string-helper-functions';
import { emptyGuid } from '../../../webmodule-common/other/api/guid';
import { currentUserClaims } from '../../../webmodule-common/other/currentuser-claims';

@customElement('wm-supportinboxview')
export class SupportInboxView extends DataEntryTabManagerView {
  protected franchiseeApi: FranchiseeNetworkApi = getApiFactory().franchisee();
  @state()
  protected viewAll?: QuoteSupportInboxViewTab;
  @state()
  protected viewReviews?: QuoteSupportInboxViewTab;
  @state()
  protected viewSupport?: QuoteSupportInboxViewTab;
  @state()
  protected viewCustomItems?: QuoteSupportInboxViewTab;
  @state()
  protected viewArchived?: QuoteSupportInboxViewTab;

  private territoryManager = getTerritoryManager();
  activeTerritoryBranchIds: string[] | undefined;

  public async afterConstruction(): Promise<void> {
    await this.territoryManager.needsTerritories();
    const userSecurity = currentUserClaims();
    let territoryId = userSecurity.claim(claimIdentity.supplierDealerTerritory);
    if (!validId(territoryId)) {
      territoryId = emptyGuid;
    }
    this.activeTerritoryBranchIds = !validId(territoryId)
      ? undefined
      : this.territoryManager.getBranchLinksByTerritory(territoryId)?.map(x => x.branchId);

    this.viewAll = await constructAsync(
      new QuoteSupportInboxViewTab({
        tabName: 'all',
        caption: tlang`Current (All)`,
        statusFilter:
          // eslint-disable-next-line no-bitwise
          SupportInboxItemStatus.Active |
          SupportInboxItemStatus.ReActivated |
          SupportInboxItemStatus.New |
          SupportInboxItemStatus.Overdue,
        typeFilter: SupportInboxItemType.All,
        eventLogin: item => loginAsFranchiseeForSupportTicket(item),
        showAssignedTo: true,
        showLastModified: true,
        activeTerritoryBranchIds: this.activeTerritoryBranchIds
      })
    );
    this.viewReviews = await constructAsync(
      new QuoteSupportInboxViewTab({
        tabName: 'quotereviews',
        caption: tlang`%%quote%% Reviews`,
        statusFilter:
          // eslint-disable-next-line no-bitwise
          SupportInboxItemStatus.Active |
          SupportInboxItemStatus.New |
          SupportInboxItemStatus.ReActivated |
          SupportInboxItemStatus.Overdue,
        typeFilter: SupportInboxItemType.QuoteReview,
        eventLogin: item => loginAsFranchiseeForSupportTicket(item),
        showAssignedTo: true,
        showLastModified: true,
        activeTerritoryBranchIds: this.activeTerritoryBranchIds
      })
    );
    this.viewSupport = await constructAsync(
      new QuoteSupportInboxViewTab({
        tabName: 'quotesupport',
        caption: `${getSupportTypeDisplayValue(SupportInboxItemType.QuoteAssistanceRequest)}s`,
        statusFilter:
          // eslint-disable-next-line no-bitwise
          SupportInboxItemStatus.Active |
          SupportInboxItemStatus.New |
          SupportInboxItemStatus.ReActivated |
          SupportInboxItemStatus.Overdue,
        typeFilter: SupportInboxItemType.QuoteAssistanceRequest,
        eventLogin: item => loginAsFranchiseeForSupportTicket(item),
        showAssignedTo: true,
        showLastModified: true,
        activeTerritoryBranchIds: this.activeTerritoryBranchIds
      })
    );
    this.viewCustomItems = await constructAsync(
      new QuoteSupportInboxViewTab({
        tabName: 'customquoteitem',
        caption: `${getSupportTypeDisplayValue(SupportInboxItemType.EngineeredToOrder)}s`,
        statusFilter:
          // eslint-disable-next-line no-bitwise
          SupportInboxItemStatus.Active |
          SupportInboxItemStatus.New |
          SupportInboxItemStatus.ReActivated |
          SupportInboxItemStatus.Overdue,
        typeFilter: SupportInboxItemType.EngineeredToOrder,
        eventLogin: item => loginAsFranchiseeForSupportTicket(item),
        showAssignedTo: true,
        showLastModified: true,
        activeTerritoryBranchIds: this.activeTerritoryBranchIds
      })
    );
    this.viewArchived = await constructAsync(
      new QuoteSupportInboxViewTab({
        tabName: 'archived',
        caption: tlang`Archived`,
        // eslint-disable-next-line no-bitwise
        statusFilter: SupportInboxItemStatus.Cancelled | SupportInboxItemStatus.Resolved,
        typeFilter: SupportInboxItemType.All,
        eventLogin: item => loginAsFranchiseeForSupportTicket(item),
        showResolvedColumns: true,
        activeTerritoryBranchIds: this.activeTerritoryBranchIds
      })
    );

    await super.afterConstruction();
  }

  protected getPagesForTabManager(): TabPage[] {
    // The inbox renders its per-tab refresh button bottom-right (its legacy
    // pageButtonLocation was right).
    const rightButtons = { buttonRegion: 'primary' as const };
    const list: TabPage[] = [];

    if (this.viewAll) list.push(this.viewAll.createTabPage(undefined, rightButtons));
    if (this.viewReviews) list.push(this.viewReviews.createTabPage(undefined, rightButtons));
    if (this.viewSupport) list.push(this.viewSupport.createTabPage(undefined, rightButtons));
    if (this.viewCustomItems) list.push(this.viewCustomItems.createTabPage(undefined, rightButtons));
    if (this.viewArchived) list.push(this.viewArchived.createTabPage(undefined, rightButtons));
    return list;
  }

  protected createTabManager(): TabManager {
    return new TabManager({
      initialIndex: 0,
      pages: () => this.getPagesForTabManager()
    });
  }

  async prepareForSave(): Promise<void> {
    //DOES NOTHING HANDLED BY CHILDREN
  }

  public internalDataChanged(): boolean {
    return false;
  }
}
